import { ref } from 'vue'
import { check } from '@tauri-apps/plugin-updater'
import { relaunch } from '@tauri-apps/plugin-process'
import { logger } from '@/lib/logger'

export interface UpdateStatus {
  checking: boolean
  available: boolean
  downloading: boolean
  progress: number
  version: string | null
  error: string | null
}

const updateStatus = ref<UpdateStatus>({
  checking: false,
  available: false,
  downloading: false,
  progress: 0,
  version: null,
  error: null
})

export function useAppUpdater() {
  /**
   * Check for available updates
   * @returns true if an update is available
   */
  const checkForUpdates = async (): Promise<boolean> => {
    updateStatus.value.checking = true
    updateStatus.value.error = null
    
    try {
      const update = await check()
      
      if (update) {
        updateStatus.value.available = true
        updateStatus.value.version = update.version
        logger.info(`Update available: ${update.version}`)
        return true
      }
      
      logger.info('No updates available')
      return false
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      updateStatus.value.error = message
      logger.error('Failed to check for updates:', error)
      return false
    } finally {
      updateStatus.value.checking = false
    }
  }

  /**
   * Download and install the available update
   */
  const downloadAndInstall = async (): Promise<void> => {
    updateStatus.value.downloading = true
    updateStatus.value.progress = 0
    updateStatus.value.error = null

    try {
      const update = await check()
      
      if (!update) {
        throw new Error('No update available')
      }

      let downloaded = 0
      let contentLength = 0

      await update.downloadAndInstall((event) => {
        switch (event.event) {
          case 'Started':
            contentLength = event.data.contentLength ?? 0
            logger.info(`Download started, total size: ${contentLength}`)
            break
          case 'Progress':
            downloaded += event.data.chunkLength
            if (contentLength > 0) {
              updateStatus.value.progress = Math.round((downloaded / contentLength) * 100)
            }
            break
          case 'Finished':
            logger.info('Download finished')
            break
        }
      })

      logger.info('Update installed, relaunching...')
      await relaunch()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      updateStatus.value.error = message
      logger.error('Failed to download/install update:', error)
      throw error
    } finally {
      updateStatus.value.downloading = false
    }
  }

  /**
   * Check for updates silently on app start
   */
  const checkOnStartup = async (): Promise<void> => {
    // Wait a bit before checking to not block app startup
    await new Promise(resolve => setTimeout(resolve, 3000))
    await checkForUpdates()
  }

  return {
    updateStatus,
    checkForUpdates,
    downloadAndInstall,
    checkOnStartup
  }
}
